<template>
    <el-container class="list-page">
        <el-header class="document-list-toolbar">
            <div class="document-toolbar-header">
                <div class="list-page-search">
                    <el-input prefix-icon="el-icon-search" v-model="searchContent"
                              :placeholder="searchPlaceholder"></el-input>
                </div>
                <div class="list-page-header-actions">
                    <el-button class="list-page-add-button" type="primary" :disabled="cantAddInvoice" @click.native.prevent="goAddInvoice">
                        <i class="el-icon-plus"></i>
                        <span>{{addButtonText}}</span>
                    </el-button>
                </div>
            </div>
            <div v-if="loadError === '' && companyContextLoaded" class="invoice-filter-row" v-loading="loading">
                <div class="invoice-filter-controls">
                    <el-select class="invoice-client-select" v-model="clientUUID" clearable filterable remote
                               :remote-method="searchClientOptions" :loading="clientOptionsLoading"
                               :placeholder="$t('invoice.filterAllClients')" @change="changeListFilter">
                        <el-option v-for="client in clientOptions" :key="client.innerUUID"
                                   :label="client.name" :value="client.innerUUID"></el-option>
                    </el-select>
                    <el-select class="invoice-status-select" v-model="selectedStatus" @change="changeListFilter">
                        <el-option :label="$t('estimate.filterAllStatuses')" value=""></el-option>
                        <el-option v-for="statusOption in statusOptions" :key="statusOption.value"
                                   :label="statusOption.label" :value="statusOption.value"></el-option>
                    </el-select>
                    <el-date-picker class="invoice-date-range" v-model="documentDateRange" type="daterange"
                                    unlink-panels format="MMM d,yyyy"
                                    :start-placeholder="$t('invoice.filterStartDate')"
                                    :end-placeholder="$t('invoice.filterEndDate')"
                                    @change="changeListFilter"></el-date-picker>
                    <el-select class="invoice-year-select" v-model="selectedYear"
                               :disabled="loading || savingSettings" @change="changeYearFilter">
                        <el-option v-for="yearOption in yearOptions" :key="yearOption.key"
                                   :label="yearOption.label" :value="yearOption.value"></el-option>
                    </el-select>
                </div>
            </div>
        </el-header>
        <el-main class="list-page-main" v-loading="loading">
            <el-alert v-if="loadError !== ''" :title="loadError" type="error" :closable="false" show-icon></el-alert>
            <template v-else-if="companyContextLoaded">
                <el-alert v-if="clientOptionsLoadError !== ''" :title="clientOptionsLoadError" type="error"
                          :closable="false" show-icon></el-alert>
                <list-batch-actions :selected-count="selectedRows.length" :actions="batchActions"
                                    :loading-action="batchLoadingAction" @action="handleBatchAction"></list-batch-actions>
                <div>
                    <div class="list-table-card document-list-desktop-table">
                    <el-table ref="listTable" class="list-table" @sort-change="sortTable"
                              :data="tableData" row-key="innerUUID" @selection-change="handleSelectionChange"
                              @select-all="handleSelectAll"
                              :empty-text="$t('common.noData')"
                              :default-sort="{prop: orderName, order: orderTpe === 1 ? 'ascending' : 'descending'}"
                              @row-click='handleRowHandle'>
                        <el-table-column type="selection" width="52"
                                         :reserve-selection="true"></el-table-column>
                        <el-table-column prop="numPrefix,num" :label="$t('estimate.columnNumber')" sortable="custom" :sort-orders="['descending', 'ascending']" min-width="130"
                                         class-name="list-no-ellipsis-column">
                            <template slot="header">
                                <span class="list-sort-header" :class="{'is-active': orderName === 'numPrefix,num'}">
                                    {{$t('estimate.columnNumber')}}
                                    <svg class="list-sort-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                        <path :class="{'is-active': orderName === 'numPrefix,num' && orderTpe === 1}" d="m8 9 4-4 4 4"/>
                                        <path :class="{'is-active': orderName === 'numPrefix,num' && orderTpe === 2}" d="m8 15 4 4 4-4"/>
                                    </svg>
                                </span>
                            </template>
                            <template slot-scope="scope"> {{scope.row.numPrefix}}{{scope.row.num}}</template>
                        </el-table-column>
                        <el-table-column prop="userClientName" :label="$t('estimate.columnClient')" sortable="custom"
                                         :sort-orders="['descending', 'ascending']" min-width="150">
                            <template slot="header">
                                <span class="list-sort-header" :class="{'is-active': orderName === 'userClientName'}">
                                    {{$t('estimate.columnClient')}}
                                    <svg class="list-sort-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                        <path :class="{'is-active': orderName === 'userClientName' && orderTpe === 1}" d="m8 9 4-4 4 4"/>
                                        <path :class="{'is-active': orderName === 'userClientName' && orderTpe === 2}" d="m8 15 4 4 4-4"/>
                                    </svg>
                                </span>
                            </template>
                            <template slot-scope="scope">
                                <span class="list-client-name" v-if="scope.row.userClientName">{{scope.row.userClientName}}</span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="invoiceStartDate" :label="$t('estimate.columnDate')" :formatter="dateFormat" sortable="custom" :sort-orders="['descending', 'ascending']" min-width="150">
                            <template slot="header">
                                <span class="list-sort-header" :class="{'is-active': orderName === 'invoiceStartDate'}">
                                    {{$t('estimate.columnDate')}}
                                    <svg class="list-sort-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                        <path :class="{'is-active': orderName === 'invoiceStartDate' && orderTpe === 1}" d="m8 9 4-4 4 4"/>
                                        <path :class="{'is-active': orderName === 'invoiceStartDate' && orderTpe === 2}" d="m8 15 4 4 4-4"/>
                                    </svg>
                                </span>
                            </template>
                        </el-table-column>
                    <!--<el-table-column prop="dueDate" label="Due date" sortable="custom">
                        <template scope="scope">
                            {{formatDueDate2(scope.row.dueDate,scope)}}
                            <el-tag size="mini" color="#1fca34" style="color: white" v-if="scope.row.paidStatus=== 2 && scope.row.totalMoney>0">Fully paid</el-tag>
                            <el-tag size="mini" color="#e8a944" style="color: white" v-else-if="scope.row.paidStatus=== 1 && Date.parse(new Date())<=scope.row.dueDate*1000">Partial</el-tag>
                            <el-tag color="#F42E46" style="color: white" size="mini" v-else-if="Date.parse(new Date())>scope.row.dueDate*1000 && scope.row.totalMoney>0">
                                Overdue
                            </el-tag>
                        </template>
                    </el-table-column>-->
                        <el-table-column prop="emailed" :label="$t('estimate.columnEmail')" width="100"
                                         align="center" header-align="center"
                                         class-name="list-status-column"
                                         label-class-name="list-status-column">
                            <template slot-scope="scope">
                                <span class="list-status-badge list-status-blue" v-if="scope.row.emailed=== 0">{{$t('estimate.statusUnsent')}}</span>
                                <span class="list-status-badge list-status-green" v-else-if="scope.row.emailed=== 1">{{$t('estimate.statusSent')}}</span>
                                <span class="list-status-badge list-status-purple" v-else-if="scope.row.emailed=== 2">{{$t('invoice.statusOpened')}}</span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="status" :label="$t('estimate.columnStatus')" min-width="130">
                        </el-table-column>

                        <el-table-column prop="totalMoney" :formatter="amountFormat" :label="$t('estimate.columnTotal')" sortable="custom" :sort-orders="['descending', 'ascending']" min-width="140" align="right" header-align="right">
                            <template slot="header">
                                <span class="list-sort-header" :class="{'is-active': orderName === 'totalMoney'}">
                                    {{$t('estimate.columnTotal')}}
                                    <svg class="list-sort-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                        <path :class="{'is-active': orderName === 'totalMoney' && orderTpe === 1}" d="m8 9 4-4 4 4"/>
                                        <path :class="{'is-active': orderName === 'totalMoney' && orderTpe === 2}" d="m8 15 4 4 4-4"/>
                                    </svg>
                                </span>
                            </template>
                        </el-table-column>
                        <el-table-column width="176" align="right" header-align="right"
                                         class-name="list-action-column"
                                         label-class-name="list-action-column-header">
                            <template slot-scope="scope">
                                <div class="invoice-list-action-group" @click.stop>
                                    <button type="button" class="invoice-list-quick-action" :aria-label="$t('common.print')"
                                            :title="$t('common.print')" @click.stop="printDocument(scope.row)">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                            <path d="M6 9V2h12v7"/>
                                            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                                            <rect x="6" y="14" width="12" height="8"/>
                                        </svg>
                                    </button>
                                    <button type="button" class="invoice-list-quick-action" :aria-label="$t('common.send')"
                                            :title="$t('common.send')" @click.stop="sendDocument(scope.row)">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                            <path d="m22 2-7 20-4-9-9-4 20-7Z"/>
                                            <path d="M22 2 11 13"/>
                                        </svg>
                                    </button>
                                    <el-dropdown trigger="click" @command="handleEstimateAction($event, scope.row)" @click.native.stop>
                                        <button type="button" class="invoice-list-action-button" @click.stop :aria-label="$t('common.moreActions')">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2F77F1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                                <circle cx="12" cy="12" r="1"/>
                                                <circle cx="12" cy="5" r="1"/>
                                                <circle cx="12" cy="19" r="1"/>
                                            </svg>
                                        </button>
                                        <el-dropdown-menu slot="dropdown" class="invoice-list-action-menu">
                                            <el-dropdown-item command="markAsSent">{{$t('estimate.actionMarkAsSent')}}</el-dropdown-item>
                                            <el-dropdown-item command="copy" divided>{{$t('estimate.actionCopy')}}</el-dropdown-item>
                                            <el-dropdown-item command="convertToInvoice">{{$t('estimate.actionConvertToInvoice')}}</el-dropdown-item>
                                            <el-dropdown-item command="delete">{{$t('estimate.actionDelete')}}</el-dropdown-item>
                                        </el-dropdown-menu>
                                    </el-dropdown>
                                </div>
                            </template>
                        </el-table-column>
                    </el-table>
                    </div>
                    <div class="document-list-mobile-list">
                        <div v-if="tableData.length === 0" class="document-list-mobile-empty">{{$t('common.noData')}}</div>
                        <article v-for="document in tableData" :key="document.innerUUID"
                                 class="document-list-mobile-card selection-mode">
                            <el-checkbox class="document-list-mobile-checkbox" :value="isRowSelected(document)"
                                         :aria-label="$t('common.select')"
                                         @change="toggleDocumentSelection(document, $event)"></el-checkbox>
                            <button type="button" class="document-list-mobile-content" @click="openDocument(document, $event)">
                                <span class="document-list-mobile-heading">
                                    <strong>{{document.numPrefix}}{{document.num}}</strong>
                                    <strong>{{formatDocumentAmount(document)}}</strong>
                                </span>
                                <span class="document-list-mobile-client">{{document.userClientName}}</span>
                                <span class="document-list-mobile-facts">
                                    <span><small>{{$t('estimate.columnDate')}}</small>{{dateFormat(document, {property: 'invoiceStartDate'})}}</span>
                                    <span><small>{{$t('estimate.columnEmail')}}</small>
                                        <span class="list-status-badge list-status-blue" v-if="document.emailed === 0">{{$t('estimate.statusUnsent')}}</span>
                                        <span class="list-status-badge list-status-green" v-else-if="document.emailed === 1">{{$t('estimate.statusSent')}}</span>
                                        <span class="list-status-badge list-status-purple" v-else>{{$t('invoice.statusOpened')}}</span>
                                    </span>
                                    <span><small>{{$t('estimate.columnStatus')}}</small>{{document.status}}</span>
                                </span>
                            </button>
                            <div class="invoice-list-action-group document-list-mobile-actions" @click.stop>
                                <button type="button" class="invoice-list-quick-action" :aria-label="$t('common.print')"
                                        :title="$t('common.print')" @click.stop="printDocument(document)">
                                    <i class="el-icon-printer" aria-hidden="true"></i>
                                </button>
                                <button type="button" class="invoice-list-quick-action" :aria-label="$t('common.send')"
                                        :title="$t('common.send')" @click.stop="sendDocument(document)">
                                    <i class="el-icon-position" aria-hidden="true"></i>
                                </button>
                                <el-dropdown trigger="click" @command="handleEstimateAction($event, document)" @click.native.stop>
                                    <button type="button" class="invoice-list-action-button" :aria-label="$t('common.moreActions')">
                                        <i class="el-icon-more" aria-hidden="true"></i>
                                    </button>
                                    <el-dropdown-menu slot="dropdown" class="invoice-list-action-menu">
                                        <el-dropdown-item command="markAsSent">{{$t('estimate.actionMarkAsSent')}}</el-dropdown-item>
                                        <el-dropdown-item command="copy" divided>{{$t('estimate.actionCopy')}}</el-dropdown-item>
                                        <el-dropdown-item command="convertToInvoice">{{$t('estimate.actionConvertToInvoice')}}</el-dropdown-item>
                                        <el-dropdown-item command="delete">{{$t('estimate.actionDelete')}}</el-dropdown-item>
                                    </el-dropdown-menu>
                                </el-dropdown>
                            </div>
                        </article>
                    </div>
                </div>
                <div v-if="totalCount > 0" class="list-pagination">
                    <el-pagination
                            :key="getListPaginationName() + '-pagination-' + currentPage + '-' + pageSize"
                            @size-change="changeTotalCount"
                            @current-change="changePage"
                            :current-page="currentPage"
                            :page-sizes="[50, 100, 200, 400]"
                            :page-size="pageSize"
                            layout="total, sizes, prev, pager, next, jumper"
                            :total="totalCount">
                    </el-pagination>
                </div>

            </template>
        </el-main>
        <el-dialog :title="$t('estimate.copyDialogTitle', {document: documentName})" :visible.sync="copyDialog" width="760px"
                   custom-class="document-copy-dialog" :close-on-click-modal="false">
            <div class="document-copy-form">
                <div class="document-copy-section">
                    <div class="document-copy-section-title">{{$t('estimate.copyTargetType')}}</div>
                    <el-radio-group class="document-copy-targets" v-model="copyForm.targetType">
                        <el-radio class="document-copy-target" :label="0">
                            <span class="document-copy-target-content"><i class="el-icon-document"></i>{{$t('default.invoice')}}</span>
                        </el-radio>
                        <el-radio class="document-copy-target" :label="1">
                            <span class="document-copy-target-content"><i class="el-icon-date"></i>{{$t('default.estimate')}}</span>
                        </el-radio>
                        <el-radio class="document-copy-target" :label="2">
                            <span class="document-copy-target-content"><i class="el-icon-tickets"></i>{{$t('default.purchaseOrder')}}</span>
                        </el-radio>
                        <el-radio class="document-copy-target" :label="3">
                            <span class="document-copy-target-content"><i class="el-icon-document-delete"></i>{{$t('default.creditMemo')}}</span>
                        </el-radio>
                    </el-radio-group>
                </div>
                <div class="document-copy-section">
                    <div class="document-copy-section-title">{{$t('estimate.copyOptions')}}</div>
                    <div class="document-copy-options">
                        <el-checkbox class="document-copy-option" v-model="copyForm.copyClient">
                            <span><i class="el-icon-user"></i>{{$t('estimate.copyClient')}}</span>
                        </el-checkbox>
                        <el-checkbox class="document-copy-option" v-model="copyForm.copyItems">
                            <span><i class="el-icon-goods"></i>{{$t('estimate.copyItems')}}</span>
                        </el-checkbox>
                        <el-checkbox class="document-copy-option" v-model="copyForm.copyAttachments">
                            <span><i class="el-icon-paperclip"></i>{{$t('estimate.copyAttachments')}}</span>
                        </el-checkbox>
                    </div>
                </div>
            </div>
            <div slot="footer" class="dialog-footer document-copy-footer">
                <el-button @click="copyDialog = false">{{$t('common.cancel')}}</el-button>
                <el-button type="primary" :loading="copyLoading" @click="performCopy">{{$t('estimate.actionCopy')}}</el-button>
            </div>
        </el-dialog>
        <document-quick-actions ref="documentQuickActions" :create-type="pdfCreateType" :document-name="documentName"
                                :company-id="companyUUID" :company-name="companyName" :setting="userSetting"
                                @sent="handleQuickSendSuccess"></document-quick-actions>
    </el-container>
</template>

<script>
    import {formatEnglishDate, getDaysByDateString} from '@/utils/time'
    import {timest, toTmpStartOfDayTime} from '@/utils/validate'
    import {debounce} from '@/utils/debounce'
    import {xiaoshudianfenge} from '@/utils/moneyFormat'
    import {replaceStoredSetting, syncCompanySelection} from '@/utils/company'
    import {getBrowserStartOfTodayEpochSecond, getBrowserTimeZone} from '@/utils/browserTimeZone'
    import {
        getStoredCurrentPlanName,
        getStoredDocumentCreationAccess,
        openFastSpringPage,
        SUBSCRIPTION_ACCESS_ALLOWED,
        SUBSCRIPTION_ACCESS_PREMIUM_REQUIRED
    } from '@/utils/subscription'
    import {readListPagination, writeListPagination} from '@/utils/listPagination'
    import {readListQueryState, writeListQueryState} from '@/utils/listQueryState'
    import {validateDocumentDetailResponse} from '@/utils/documentDetailValidation'
    import {
        ALL_DOCUMENT_YEARS,
        buildDocumentPreferenceParams,
        documentSortColumn,
        readDocumentPreferences,
        validateDocumentPreferences
    } from '@/utils/documentList'
    import {openListDetail} from '@/utils/listNavigation'
    import DocumentQuickActions from '@/components/common/DocumentQuickActions'
    import ListBatchActions from '@/components/common/ListBatchActions'

    export default {
        components: {
            DocumentQuickActions,
            ListBatchActions
        },
        methods: {
            printDocument(row) {
                this.$refs.documentQuickActions.printDocument(row);
            },
            sendDocument(row) {
                this.$refs.documentQuickActions.openSendDialog(row);
            },
            handleSelectionChange(selection) {
                this.selectedRows = selection;
            },
            handleSelectAll(selection) {
                const selectedUUIDSet = new Set(selection.map(row => row.innerUUID));
                const allCurrentPageSelected = this.tableData.length > 0
                    && this.tableData.every(row => selectedUUIDSet.has(row.innerUUID));
                if (!allCurrentPageSelected) {
                    this.clearSelection();
                }
            },
            isRowSelected(row) {
                return this.selectedRows.some(selected => selected.innerUUID === row.innerUUID);
            },
            toggleDocumentSelection(row, selected) {
                if (this.$refs.listTable) {
                    this.$refs.listTable.toggleRowSelection(row, selected);
                    return;
                }
                if (selected && !this.isRowSelected(row)) {
                    this.selectedRows = this.selectedRows.concat(row);
                } else if (!selected) {
                    this.selectedRows = this.selectedRows.filter(item => item.innerUUID !== row.innerUUID);
                }
            },
            clearSelection() {
                this.selectedRows = [];
                if (this.$refs.listTable) {
                    this.$refs.listTable.clearSelection();
                }
            },
            handleQuickSendSuccess() {
                this.clearSelection();
                this.queryList();
            },
            handleBatchAction(action) {
                if (action === 'print') {
                    this.$refs.documentQuickActions.printDocuments(this.selectedRows);
                    return;
                }
                if (action === 'sendEmail') {
                    this.$refs.documentQuickActions.openBatchSendDialog(this.selectedRows);
                    return;
                }
                if (action === 'delete') {
                    this.$confirm(this.$t('common.batchDeleteConfirm', {count: this.selectedRows.length}), this.$t('common.warning'), {
                        confirmButtonText: this.$t('common.ok'),
                        cancelButtonText: this.$t('common.cancel'),
                        type: 'warning'
                    }).then(() => this.executeBatchMutation('DELETE', action)).catch(() => {});
                    return;
                }
                this.executeBatchMutation('MARK_SENT', action);
            },
            executeBatchMutation(action, loadingAction) {
                this.batchLoadingAction = loadingAction;
                return this.$axios.post('web_batch_action', {
                    params: {
                        entityType: 'DOCUMENT',
                        action: action,
                        documentType: this.invoiceType,
                        companyUUID: this.companyUUID,
                        innerUUIDList: this.selectedRows.map(row => row.innerUUID)
                    }
                }).then(response => {
                    if (response.data.errorCode === 0) {
                        this.$message.success(this.$t('common.batchActionSuccess', {count: response.data.processedCount}));
                        if (action === 'DELETE') {
                            this.moveToExistingPage(response.data.processedCount);
                        }
                        this.clearSelection();
                        this.queryList();
                    }
                }).finally(() => {
                    this.batchLoadingAction = '';
                });
            },
            moveToExistingPage(deletedCount) {
                const remainingCount = Math.max(0, this.totalCount - deletedCount);
                const lastPage = Math.max(1, Math.ceil(remainingCount / this.pageSize));
                if (this.currentPage > lastPage) {
                    this.currentPage = lastPage;
                    this.saveListPagination();
                }
            },
            getListPaginationName() {
                if (this.invoiceType === 2) return 'purchaseOrder';
                if (this.invoiceType === 3) return 'creditMemo';
                return 'estimate';
            },
            restoreListPagination() {
                const pagination = readListPagination(this.getListPaginationName());
                if (pagination != null) {
                    this.currentPage = pagination.currentPage;
                    this.pageSize = pagination.pageSize;
                }
            },
            restoreListQueryState() {
                const queryState = readListQueryState(this.getListPaginationName());
                if (queryState == null) {
                    return;
                }
                this.currentPage = queryState.currentPage;
                this.pageSize = queryState.pageSize;
                this.searchContent = queryState.searchContent;
                this.selectedStatus = queryState.selectedStatus;
                this.selectedYear = queryState.selectedYear;
                this.documentDateRange = queryState.documentDateRange == null
                    ? null : queryState.documentDateRange.map(timestamp => new Date(timestamp));
                this.clientUUID = queryState.clientUUID;
                this.orderName = queryState.orderName;
                this.orderTpe = queryState.orderTpe;
            },
            saveListQueryState() {
                writeListQueryState(this.getListPaginationName(), {
                    currentPage: this.currentPage,
                    pageSize: this.pageSize,
                    searchContent: this.searchContent,
                    selectedStatus: this.selectedStatus,
                    selectedYear: this.selectedYear,
                    documentDateRange: this.documentDateRange == null
                        ? null : this.documentDateRange.map(date => new Date(date).getTime()),
                    clientUUID: this.clientUUID,
                    orderName: this.orderName,
                    orderTpe: this.orderTpe
                });
            },
            saveListPagination() {
                writeListPagination(this.getListPaginationName(), this.currentPage, this.pageSize);
            },
            applyDocumentPreferences(setting) {
                const preferences = readDocumentPreferences(setting, this.invoiceType);
                this.settings = preferences;
                this.selectedYear = preferences.filterYear === ALL_DOCUMENT_YEARS
                    ? null
                    : Number(preferences.filterYear);
                this.orderName = documentSortColumn(preferences.sortType);
                this.orderTpe = preferences.sortAscending === 1 ? 1 : 2;
            },
            async persistDocumentPreferences(preferences) {
                preferences = validateDocumentPreferences(preferences);
                const response = await this.$axios.post('update_setting', {
                    params: Object.assign({
                        innerUUID: this.userSetting.innerUUID,
                        companyUUID: this.companyUUID,
                        accessDate: timest()
                    }, buildDocumentPreferenceParams(preferences, this.invoiceType))
                });
                if (response.data.errorCode !== 0) {
                    throw new Error('Updated document preferences are invalid');
                }
                this.userSetting = response.data.objectData == null
                    ? Object.assign({}, this.userSetting, buildDocumentPreferenceParams(preferences, this.invoiceType))
                    : response.data.objectData;
                const updatedPreferences = readDocumentPreferences(this.userSetting, this.invoiceType);
                replaceStoredSetting(this.userSetting);
                this.settings = updatedPreferences;
                this.applyDocumentPreferences(this.userSetting);
            },
            async changeYearFilter() {
                if (!this.companyContextLoaded || this.loading || this.savingSettings) {
                    return;
                }
                const previousYear = this.settings.filterYear;
                const preferences = Object.assign({}, this.settings, {
                    filterYear: this.selectedYear === null ? ALL_DOCUMENT_YEARS : String(this.selectedYear)
                });
                this.savingSettings = true;
                try {
                    await this.persistDocumentPreferences(preferences);
                    this.clearSelection();
                    this.currentPage = 1;
                    this.saveListPagination();
                    await this.queryList();
                } catch (error) {
                    this.selectedYear = previousYear === ALL_DOCUMENT_YEARS ? null : Number(previousYear);
                    this.$message.error(error.message);
                } finally {
                    this.savingSettings = false;
                }
            },
            goAddInvoice() {
                const access = getStoredDocumentCreationAccess();
                if (access !== SUBSCRIPTION_ACCESS_ALLOWED) {
                    openFastSpringPage(
                        getStoredCurrentPlanName(),
                        access === SUBSCRIPTION_ACCESS_PREMIUM_REQUIRED ? 'Premium' : 'Pro'
                    );
                    return;
                }

                let self = this;
                self.$router.push({path: '/createEstimate', query: {type: this.invoiceType}});
            },
            queryCompanyAndInvoiceList() {
                let that = this;
                this.listRequestSequence += 1;
                this.loading = true;
                this.cantAddInvoice = true;
                this.companyContextLoaded = false;
                this.loadError = '';
                this.$axios.post('query_setting_and_company', {
                    params: {}
                }).then(response => {
                    if (response.data.errorCode === 0) {
                        const selectedData = syncCompanySelection(response.data);
                        if (selectedData == null) {
                            throw new Error('Selected company data is missing');
                        }
                        that.companyUUID = selectedData.company.innerUUID;
                        that.companyName = selectedData.company.companyName;
                        that.restoreListPagination();
                        that.userSetting = selectedData.setting;
                        that.applyDocumentPreferences(that.userSetting);
                        that.restoreListQueryState();
                        that.$nextTick(() => {
                            that.searchWatcherPaused = false;
                        });
                        that.companyContextLoaded = true;
                        that.loadInitialClientOptions();
                        that.queryList();
                        return;
                    }
                    that.loading = false;
                    that.loadError = that.$t('common.loadFailed');
                }).catch(function () {
                    that.loading = false;
                    that.loadError = that.$t('common.loadFailed');
                })
            },
            queryList() {
                const requestSequence = ++this.listRequestSequence;
                let that = this;
                this.loading = true;
                this.loadError = "";
                this.cantAddInvoice = true;
                this.$axios.post('query_invoice_list_user_web', {
                    params: this.buildInvoiceListParams()
                }).then(response => {
                    if (requestSequence !== that.listRequestSequence) {
                        return;
                    }
                    if (response.data.errorCode !== 0) {
                        throw new Error('Document list query failed');
                    }
                    const dataList = Array.isArray(response.data.dataList) ? response.data.dataList : [];
                    that.tableData = dataList.filter(document => document != null
                        && (document.companyUUID == null || document.companyUUID === that.companyUUID)
                        && (document.type == null || Number(document.type) === Number(that.invoiceType))
                        && Number(document.syncStatus) !== 1);
                    const totalCount = Number(response.data.totalCount);
                    that.totalCount = Number.isFinite(totalCount) && totalCount >= 0
                        ? Math.floor(totalCount) : that.tableData.length;
                    that.totalTypeCount = response.data.totalTypeCount;
                    that.yearList = Array.from(new Set((Array.isArray(response.data.yearList)
                        ? response.data.yearList : []).map(Number).filter(year =>
                        Number.isInteger(year) && year >= 1970 && year <= 3000))).sort((left, right) => right - left);
                    that.loading = false;
                    that.cantAddInvoice = false;

                }).catch(() => {
                    if (requestSequence === that.listRequestSequence) {
                        that.loading = false;
                        that.cantAddInvoice = false;
                        that.loadError = that.$t('common.loadFailed');
                    }
                })
            },
            buildInvoiceListParams() {
                const params = {
                    currentPage: this.currentPage,
                    pageSize: this.pageSize,
                    invoiceNumberQuery: this.searchContent,
                    orderName: this.orderName,
                    orderTpe: this.orderTpe,
                    type: this.invoiceType,
                    companyUUID: this.companyUUID,
                    timePeriodType: this.timePeriodType,
                    nowTime: getBrowserStartOfTodayEpochSecond(),
                    timeZone: getBrowserTimeZone()
                };
                if (this.selectedYear !== null) {
                    params.year = this.selectedYear;
                }
                if (this.selectedStatus === 'EMAIL_UNSENT') {
                    params.emailed = 0;
                } else if (this.selectedStatus === 'EMAIL_SENT') {
                    params.emailed = 1;
                } else if (this.selectedStatus !== '') {
                    params.status = this.selectedStatus;
                }
                if (this.clientUUID !== '') {
                    params.clientUUID = this.clientUUID;
                }
                if (this.documentDateRange != null && this.documentDateRange.length === 2) {
                    params.startInvoiceDate = parseInt(toTmpStartOfDayTime(this.documentDateRange[0]), 10);
                    const endDate = new Date(this.documentDateRange[1]);
                    endDate.setDate(endDate.getDate() + 1);
                    params.endInvoiceDate = parseInt(toTmpStartOfDayTime(endDate), 10);
                }
                return params;
            },
            sortTable(column) {
                this.clearSelection();
                if (!column.order) {
                    this.orderName = documentSortColumn(this.settings.sortType);
                    this.orderTpe = this.settings.sortAscending === 1 ? 1 : 2;
                    this.queryList();
                    return;
                }
                this.orderName = column.prop;
                if (column.order === "ascending") {
                    this.orderTpe = 1
                } else if (column.order === "descending") {
                    this.orderTpe = 2
                }
                this.queryList();
            },
            changePage(val) {
                this.currentPage = val;
                this.saveListPagination();
                this.queryList();
            },
            changeTotalCount(val) {
                this.currentPage = 1;
                this.pageSize = val;
                this.saveListPagination();
                this.queryList();
            },
            changeListFilter() {
                this.clearSelection();
                this.currentPage = 1;
                this.saveListPagination();
                this.queryList();
            },
            searchClientOptions(keyword) {
                const requestSequence = ++this.clientOptionsRequestSequence;
                this.clientOptionsLoading = true;
                this.clientOptionsLoadError = '';
                this.$axios.post('query_user_client_user_web', {
                    params: {
                        currentPage: 1,
                        pageSize: 50,
                        userWebCommonQuery: keyword,
                        orderName: 'name',
                        orderTpe: 1,
                        delStatus: 0,
                        companyUUID: this.companyUUID
                    }
                }).then(response => {
                    if (requestSequence !== this.clientOptionsRequestSequence) {
                        return;
                    }
                    if (response.data.errorCode !== 0) {
                        throw new Error('Client option query failed');
                    }
                    const dataList = Array.isArray(response.data.dataList) ? response.data.dataList : [];
                    this.clientOptions = dataList.filter(client => client != null
                        && (client.companyUUID == null || client.companyUUID === this.companyUUID)
                        && Number(client.isDelete) !== 1 && Number(client.syncStatus) !== 1);
                }).catch(() => {
                    if (requestSequence === this.clientOptionsRequestSequence) {
                        this.clientOptions = [];
                        this.clientOptionsLoadError = this.$t('invoice.clientOptionsLoadFailed');
                    }
                }).finally(() => {
                    if (requestSequence === this.clientOptionsRequestSequence) {
                        this.clientOptionsLoading = false;
                    }
                });
            },
            loadInitialClientOptions() {
                this.searchClientOptions('');
            },
            handleRowHandle(row, column, event) {
                if (column && (column.type === "selection" || column.className === "list-action-column")) {
                    return false;
                }
                this.openDocument(row, event);
            },
            openDocument(row, event) {
                if (!this.companyContextLoaded || this.loading) {
                    return;
                }
                openListDetail(this.$router, {
                    path: 'updateEstimate',
                    query: {innerUUID: row.innerUUID, type: this.invoiceType}
                }, event);
            },
            handleEstimateAction(command, row) {
                if (command === 'markAsSent') {
                    this.markEstimateAsSent(row);
                    return;
                }
                if (command === 'copy') {
                    this.openCopyDialog(row);
                    return;
                }
                if (command === 'convertToInvoice') {
                    this.confirmConvertToInvoice(row);
                    return;
                }
                if (command === 'delete') {
                    this.confirmDeleteEstimate(row);
                    return;
                }
            },
            markEstimateAsSent(row) {
                return this.$axios.post('web_batch_action', {
                    params: {
                        entityType: 'DOCUMENT',
                        action: 'MARK_SENT',
                        documentType: this.invoiceType,
                        companyUUID: this.companyUUID,
                        innerUUIDList: [row.innerUUID]
                    }
                }).then(response => {
                    if (response.data.errorCode === 0) {
                        row.emailed = 1;
                        this.$message.success(this.$t('common.updateSuccess'));
                    }
                });
            },
            //时间格式化
            dateFormat: function (row, column) {
                var timestamp = row[column.property] * 1000;
                if (timestamp == undefined) {
                    return "";
                }
                return formatEnglishDate(timestamp);
            },
            //格式化过期时间
            formatDueDate: function (row, column) {
                let timestamp = row[column.property] * 1000;
                if (timestamp == undefined) {
                    return "";
                }
                let dueDays = getDaysByDateString(Date.parse(new Date()), timestamp);
                if (dueDays === 0) {
                    return this.$t('estimate.dueDateToday');
                } else if (dueDays < 0) {
                    return this.$t('estimate.dueDateDaysAgo', {days: Math.abs(dueDays)});
                } else {
                    return this.$t('estimate.dueDateInDays', {days: dueDays});
                }
            },
            //格式化过期时间
            formatDueDate2: function (row,scope) {

                if (scope.row.totalMoney<=0){
                    return "";
                }
                if (scope.row.paidStatus === 2) {
                    return "";
                }
                let timestamp = row * 1000;
                if (timestamp == undefined) {
                    return "";
                }
                let dueDays = getDaysByDateString(Date.parse(new Date()), timestamp);
                if (dueDays === 0) {
                    return this.$t('estimate.dueDateToday');
                } else if (dueDays < 0) {
                    return this.$t('estimate.dueDateDaysAgo', {days: Math.abs(dueDays)});
                } else {
                    return this.$t('estimate.dueDateInDays', {days: dueDays});
                }
            },

            //金钱格式化,增加金钱符号和分隔符
            amountFormat: function (row, column) {
                var columnAmount = row[column.property];
                if (columnAmount == undefined) {
                    return "";
                }
                return xiaoshudianfenge(columnAmount, this.userSetting);
            },
            formatDocumentAmount(document) {
                return xiaoshudianfenge(document.totalMoney, this.userSetting);
            },
            openCopyDialog(row) {
                this.copySourceInvoice = row;
                this.copyForm = {
                    targetType: this.invoiceType,
                    copyClient: true,
                    copyItems: true,
                    copyAttachments: true
                };
                this.copyDialog = true;
            },
            performCopy() {
                if (this.copySourceInvoice == null) {
                    return;
                }
                this.copyLoading = true;
                return this.$axios.post('web_query_invoice', {
                    params: {
                        innerUUID: this.copySourceInvoice.innerUUID,
                        companyUUID: this.companyUUID
                    }
                }).then(response => {
                    validateDocumentDetailResponse(response.data, this.companyUUID,
                        this.copySourceInvoice.innerUUID, [this.invoiceType]);
                    const sourceData = {
                        detail: response.data,
                        options: {
                            targetType: this.copyForm.targetType,
                            copyClient: this.copyForm.copyClient,
                            copyItems: this.copyForm.copyItems,
                            copyAttachments: this.copyForm.copyAttachments,
                            isConvert: false
                        }
                    };
                    localStorage.setItem('copySourceData', JSON.stringify(sourceData));
                    this.copyDialog = false;
                    if (this.copyForm.targetType === 0) {
                        this.$router.push('/invoiceCreate');
                    } else if (this.copyForm.targetType === 1) {
                        this.$router.push('/createEstimate?type=1');
                    } else if (this.copyForm.targetType === 2) {
                        this.$router.push('/createEstimate?type=2');
                    } else {
                        this.$router.push('/createEstimate?type=3');
                    }
                }).catch(() => {
                    this.$message.error(this.$t('estimate.copyFailed'));
                }).finally(() => {
                    this.copyLoading = false;
                });
            },
            confirmDeleteEstimate(row) {
                this.$confirm(this.$t('estimate.confirmDelete', {document: this.documentName}), this.$t('common.warning'), {
                    confirmButtonText: this.$t('common.ok'),
                    cancelButtonText: this.$t('common.cancel'),
                    type: 'warning'
                }).then(() => {
                    this.performDeleteEstimate(row);
                }).catch(() => {});
            },
            performDeleteEstimate(row) {
                this.deleteLoading = true;
                return this.$axios.post('web_batch_action', {
                    params: {
                        entityType: 'DOCUMENT',
                        action: 'DELETE',
                        documentType: this.invoiceType,
                        companyUUID: this.companyUUID,
                        innerUUIDList: [row.innerUUID]
                    }
                }).then(response => {
                    if (response.data.errorCode === 0) {
                        this.$message.success(this.$t('estimate.deleteSuccess', {document: this.documentName}));
                        this.moveToExistingPage(1);
                        this.clearSelection();
                        this.queryList();
                    }
                }).finally(() => {
                    this.deleteLoading = false;
                });
            },
            confirmConvertToInvoice(row) {
                this.$confirm(this.$t('estimate.convertConfirm', {document: this.documentName}), this.$t('common.warning'), {
                    confirmButtonText: this.$t('common.ok'),
                    cancelButtonText: this.$t('common.cancel'),
                    type: 'warning'
                }).then(() => {
                    this.performConvertToInvoice(row);
                }).catch(() => {});
            },
            performConvertToInvoice(row) {
                this.convertLoading = true;
                return this.$axios.post('web_query_invoice', {
                    params: {
                        innerUUID: row.innerUUID,
                        companyUUID: this.companyUUID
                    }
                }).then(response => {
                    validateDocumentDetailResponse(response.data, this.companyUUID, row.innerUUID, [this.invoiceType]);
                    const sourceData = {
                        detail: response.data,
                        options: {
                            targetType: 0,
                            copyClient: true,
                            copyItems: true,
                            copyAttachments: true,
                            isConvert: true
                        }
                    };
                    localStorage.setItem('copySourceData', JSON.stringify(sourceData));
                    this.$router.push('/invoiceCreate');
                }).catch(() => {
                    this.$message.error(this.$t('estimate.copyFailed'));
                }).finally(() => {
                    this.convertLoading = false;
                });
            }

        },
        created() {

        },
        computed: {
            invoiceType() {
                if (this.$route.path === '/purchaseOrder') return 2;
                if (this.$route.path === '/creditMemo') return 3;
                return 1;
            },
            pdfCreateType() {
                return this.invoiceType;
            },
            pageTitle() {
                if (this.invoiceType === 2) return this.$t('estimate.pageTitlePurchaseOrders');
                if (this.invoiceType === 3) return this.$t('estimate.pageTitleCreditMemos');
                return this.$t('estimate.pageTitleEstimates');
            },
            searchPlaceholder() {
                if (this.invoiceType === 2) return this.$t('estimate.searchPlaceholderPurchaseOrders');
                if (this.invoiceType === 3) return this.$t('estimate.searchPlaceholderCreditMemos');
                return this.$t('estimate.searchPlaceholderEstimates');
            },
            addButtonText() {
                if (this.invoiceType === 2) return this.$t('estimate.addPurchaseOrder');
                if (this.invoiceType === 3) return this.$t('estimate.addCreditMemo');
                return this.$t('estimate.addEstimate');
            },
            documentName() {
                if (this.invoiceType === 2) return this.$t('default.purchaseOrder');
                if (this.invoiceType === 3) return this.$t('default.creditMemo');
                return this.$t('default.estimate');
            },
            statusOptions() {
                return [
                    {value: 'Issued', label: this.$t('invoiceCreate.statusIssued')},
                    {value: 'Approved', label: this.$t('invoiceCreate.statusApproved')},
                    {value: 'Pending', label: this.$t('invoiceCreate.statusPending')},
                    {value: 'Withdrawn', label: this.$t('invoiceCreate.statusWithdrawn')},
                    {value: 'EMAIL_UNSENT', label: this.$t('estimate.statusUnsent')},
                    {value: 'EMAIL_SENT', label: this.$t('estimate.statusSent')}
                ];
            },
            yearOptions() {
                const yearList = [{key: 'all', label: this.$t('invoice.allYears'), value: null}];
                for (const year of this.yearList) {
                    yearList.push({key: String(year), label: String(year), value: year});
                }
                return yearList;
            },
            timePeriodType() {
                return this.selectedYear === null ? 'ALL_TIME' : 'CALENDAR_YEAR';
            },
            batchActions() {
                return [
                    {key: 'print', label: this.$t('common.print'), icon: 'el-icon-printer'},
                    {key: 'sendEmail', label: this.$t('common.sendEmail'), icon: 'el-icon-message'},
                    {key: 'markSent', label: this.$t('estimate.actionMarkAsSent'), icon: 'el-icon-position'},
                    {key: 'delete', label: this.$t('common.delete'), icon: 'el-icon-delete', danger: true}
                ];
            }
        },
        data() {
            return {
                tableData: [],
                loading: true,
                loadError: "",
                listRequestSequence: 0,
                currentPage: 1,
                totalCount: 0,
                pageSize: 50,
                searchContent: "",
                selectedStatus: "",
                selectedYear: null,
                documentDateRange: null,
                yearList: [],
                clientUUID: '',
                clientOptions: [],
                clientOptionsLoading: false,
                clientOptionsLoadError: '',
                clientOptionsRequestSequence: 0,
                selectedRows: [],
                batchLoadingAction: '',
                orderName: "invoiceStartDate",
                orderTpe: 2,
                companyUUID: '',
                companyName: '',
                companyContextLoaded: false,
                userSetting: {},
                cantAddInvoice: true,
                totalTypeCount:0,
                savingSettings: false,
                settings: {
                    filterYear: ALL_DOCUMENT_YEARS,
                    groupBy: 0,
                    sortType: 0,
                    sortAscending: 0
                },
                copyDialog: false,
                copyLoading: false,
                deleteLoading: false,
                convertLoading: false,
                copySourceInvoice: null,
                copyForm: {
                    targetType: 1,
                    copyClient: true,
                    copyItems: true,
                    copyAttachments: true
                },
                searchWatcherPaused: true
            }
        },
        mounted() {
            this.queryCompanyAndInvoiceList();
            const queryBySearchContent = debounce(() => {
                this.clearSelection();
                this.currentPage = 1;
                this.saveListPagination();
                this.queryList();
            }, 500);
            this.$watch('searchContent', () => {
                if (this.searchWatcherPaused) {
                    return;
                }
                queryBySearchContent();
            });
            this.$watch('$route.path', () => {
                this.searchWatcherPaused = true;
                this.clearSelection();
                this.selectedStatus = '';
                this.selectedYear = null;
                this.documentDateRange = null;
                this.yearList = [];
                this.clientUUID = '';
                this.clientOptions = [];
                this.searchContent = "";
                this.queryCompanyAndInvoiceList();
            })
        },
        beforeRouteLeave(to, from, next) {
            this.saveListQueryState();
            next();
        }
    }
</script>

<style scoped>
    .document-list-mobile-list { display: none; }
    @media (max-width: 768px) {
        .document-list-desktop-table { display: none; }
        .document-list-mobile-list { display: grid; gap: 12px; }
        .document-list-mobile-empty {
            min-height: 120px; padding: 40px 16px; box-sizing: border-box;
            border: 1px dashed var(--ui-border); border-radius: var(--ui-radius-panel);
            color: var(--ui-text-muted); background: var(--ui-surface); text-align: center;
        }
        .document-list-mobile-card {
            display: grid; grid-template-columns: minmax(0, 1fr); gap: 0 12px;
            padding: 16px; border: 1px solid var(--ui-border); border-radius: var(--ui-radius-panel); background: #fff;
            box-shadow: var(--ui-shadow-panel);
        }
        .document-list-mobile-card.selection-mode { grid-template-columns: auto minmax(0, 1fr); }
        .document-list-mobile-checkbox { padding-top: 2px; }
        .document-list-mobile-content {
            min-width: 0; padding: 0; border: 0; background: transparent; color: inherit;
            font: inherit; text-align: left; cursor: pointer;
        }
        .document-list-mobile-content:focus-visible { outline: 2px solid var(--ui-focus-color); outline-offset: 3px; }
        .document-list-mobile-heading { display: flex; justify-content: space-between; gap: 16px; }
        .document-list-mobile-heading strong { min-width: 0; overflow-wrap: anywhere; }
        .document-list-mobile-heading strong:last-child { flex: 0 1 auto; font-variant-numeric: tabular-nums; }
        .document-list-mobile-client {
            display: block; margin-top: 8px; color: #52627c; overflow: hidden;
            text-overflow: ellipsis; white-space: nowrap;
        }
        .document-list-mobile-facts { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; margin-top: 16px; }
        .document-list-mobile-facts > span { min-width: 0; overflow-wrap: anywhere; }
        .document-list-mobile-facts small { display: block; margin-bottom: 4px; color: var(--ui-text-muted); font-size: var(--ui-font-caption); font-weight: 600; text-transform: uppercase; }
        .document-list-mobile-actions {
            grid-column: 1; display: flex; justify-content: flex-end; gap: 8px;
            margin-top: 16px; padding-top: 12px; border-top: 1px solid #edf1f6;
        }
        .document-list-mobile-card.selection-mode .document-list-mobile-actions { grid-column: 2; }
    }

    @media (max-width: 430px) {
        .document-list-mobile-facts { grid-template-columns: minmax(0, 1fr); }
    }
</style>
